import { memo, useCallback, type ReactNode } from "react";
import type { ReportSection } from "~/api/model";
import { markdownToReportMdast } from "./mdast/markdownToMdast";
import { getSectionMdast } from "./mdast/mdastToHast";
import { ReleaseModeReportContent } from "./ReleaseModeReportContent";
import {
  ReportSectionView,
  type ReportSectionViewProps,
} from "./ReportSection";

/**
 * v2 report section renderer (§5). Reuses the entire {@link ReportSectionView}
 * shell (accordion, lazy render, edit/save, actions) and only overrides the
 * non-editing display: when the fetched section carries a valid, non-empty
 * `contentMdast` tree it renders via the MDAST pipeline, otherwise it falls
 * back to the v1 markdown renderer for that section alone (§5.1).
 *
 * Editing seeds TipTap directly from the MDAST tree and saves the structurally
 * edited result via `PATCH { contentMdast }` without a Markdown round trip.
 */
export const ReportSectionMdastView = memo((props: ReportSectionViewProps) => {
  const { terms, medicationMatcher } = props;

  const renderDisplayContent = useCallback(
    (sectionData: ReportSection | undefined): ReactNode => {
      return (
        <ReleaseModeReportContent
          content={sectionData?.content ?? ""}
          contentData={sectionData}
          terms={terms}
          medicationMatcher={medicationMatcher}
        />
      );
    },
    [terms, medicationMatcher]
  );

  const getEditMdast = useCallback(
    (sectionData: ReportSection | undefined) => getSectionMdast(sectionData),
    []
  );

  return (
    <ReportSectionView
      {...props}
      renderDisplayContent={renderDisplayContent}
      deriveContentMdast={markdownToReportMdast}
      getEditMdast={getEditMdast}
    />
  );
});

ReportSectionMdastView.displayName = "ReportSectionMdastView";
